// Round timeline and tempo clock (§5, §19.2). roundClock() is the single source of phase and beat
// information for the sim, renderer, music and bots. Every hazard schedule is expressed in beats
// and bars of this clock, so hazards stay locked to the music (integer ticks per beat, ramp and
// Sudden Death snapped to bar lines, the Sudden Death tempo after `sdStart`).
import { TICK_HZ, TUNE } from '../constants';
import { MapId, ModeId, RoundPhase } from '../ids';

export interface RoundClock { tick: number; phase: RoundPhase; beat: number; bar: number; beatInBar: number; tpb: number; bpm: number; rampStart: number; sdStart: number; cap: number }

/** Integer ticks per beat for normal play and Sudden Death, per map (§5). */
export const MAP_TEMPO: Readonly<Record<MapId, { readonly tpb: number; readonly tpbSD: number }>> = {
  [MapId.LOBBY]: { tpb: 32, tpbSD: 32 },
  [MapId.PODIUM]: { tpb: 32, tpbSD: 32 },
  [MapId.TEACUP]: { tpb: 27, tpbSD: 25 },
  [MapId.PINBALL]: { tpb: 29, tpbSD: 27 },
  [MapId.CAKE]: { tpb: 26, tpbSD: 24 },
  [MapId.SPIN]: { tpb: 30, tpbSD: 28 },
};

interface Timeline { readonly ramp: number; readonly sd: number; readonly cap: number }

/** Mode timelines in round ticks; null for the non-scoring modes (CALM forever, no cap). */
function modeTimeline(modeId: ModeId): Timeline | null {
  switch (modeId) {
    case ModeId.ROYALE: return { ramp: TUNE.royale.rampTick, sd: TUNE.royale.sdTick, cap: TUNE.royale.capTick };
    case ModeId.HOT_POTATO: return { ramp: TUNE.potato.rampTick, sd: TUNE.potato.sdTick, cap: TUNE.potato.capTick };
    case ModeId.CROWN_GRAB: return { ramp: TUNE.crown.rampTick, sd: TUNE.crown.sdTick, cap: TUNE.crown.capTick };
    default: return null;
  }
}

/**
 * The round's tempo map: both tempos, the snapped phase starts and the beat at which Sudden Death
 * starts (`sdBeat`, a whole bar). Hazard schedules convert between beats and ticks with
 * `tickAtBeat` / `beatAtTick`. Lobby and Podium have infinite phase starts.
 */
export interface Tempo {
  tpb: number; tpbSD: number;
  rampStart: number; sdStart: number; cap: number;
  /** Beat index of `rampStart` / `sdStart` (multiples of 4; Infinity without a timeline). */
  rampBeat: number; sdBeat: number;
}

export function createTempo(): Tempo {
  return { tpb: 32, tpbSD: 32, rampStart: Infinity, sdStart: Infinity, cap: Infinity, rampBeat: Infinity, sdBeat: Infinity };
}

/** Tempo map for a round (pass `out` to avoid an allocation). `capOverride` as in `roundClock`. */
export function roundTempo(mapId: MapId, modeId: ModeId, capOverride?: number, out: Tempo = createTempo()): Tempo {
  const { tpb, tpbSD } = MAP_TEMPO[mapId];
  const timeline = modeTimeline(modeId);
  out.tpb = tpb;
  out.tpbSD = tpbSD;
  out.rampStart = Infinity;
  out.sdStart = Infinity;
  out.cap = Infinity;
  if (timeline) {
    const barTicks = tpb * TUNE.clock.beatsPerBar;
    const cap = capOverride !== undefined && capOverride > 0 ? Math.round(capOverride) : timeline.cap;
    const scale = cap / timeline.cap;
    out.cap = cap;
    out.rampStart = Math.ceil((timeline.ramp * scale) / barTicks) * barTicks;
    out.sdStart = Math.ceil((timeline.sd * scale) / barTicks) * barTicks;
  }
  out.rampBeat = out.rampStart / tpb;
  out.sdBeat = out.sdStart / tpb;
  return out;
}

/** Beat (fractional) at round tick `tick`: the normal tempo before Sudden Death, then the SD tempo. */
export function beatAtTick(t: Tempo, tick: number): number {
  return tick < t.sdStart ? tick / t.tpb : t.sdBeat + (tick - t.sdStart) / t.tpbSD;
}

/** Round tick (fractional) at which beat `beat` lands; the inverse of `beatAtTick`. */
export function tickAtBeat(t: Tempo, beat: number): number {
  return beat < t.sdBeat ? beat * t.tpb : t.sdStart + (beat - t.sdBeat) * t.tpbSD;
}

/** Ticks per beat in effect at `tick`. */
export function tpbAt(t: Tempo, tick: number): number {
  return tick < t.sdStart ? t.tpb : t.tpbSD;
}

export function createRoundClock(): RoundClock {
  return { tick: 0, phase: RoundPhase.CALM, beat: 0, bar: 0, beatInBar: 0, tpb: 32, bpm: 112.5, rampStart: Infinity, sdStart: Infinity, cap: Infinity };
}

const scratchTempo = createTempo();

/**
 * Clock state at a (possibly fractional) round tick. Ramp and Sudden Death starts snap up to the
 * next bar line of the normal tempo; the cap does not snap. `capOverride` (the ?roundSec debug
 * flag) replaces the cap and scales ramp/SD proportionally. Pass `out` to avoid an allocation.
 * Lobby and Podium have no timeline: phase CALM forever and infinite ramp/SD/cap.
 */
export function roundClock(mapId: MapId, modeId: ModeId, tick: number, capOverride?: number, out: RoundClock = createRoundClock()): RoundClock {
  const t = roundTempo(mapId, modeId, capOverride, scratchTempo);
  const scoring = modeTimeline(modeId) !== null;
  const inSD = tick >= t.sdStart;
  const beat = beatAtTick(t, tick);
  const bar = Math.floor(beat / TUNE.clock.beatsPerBar);
  const curTpb = inSD ? t.tpbSD : t.tpb;

  let phase: RoundPhase = RoundPhase.CALM;
  if (scoring) {
    if (tick < 0) phase = RoundPhase.INTRO;
    else if (tick >= t.cap) phase = RoundPhase.OVER;
    else if (inSD) phase = RoundPhase.SUDDEN_DEATH;
    else if (tick >= t.rampStart) phase = RoundPhase.RAMP;
  }

  out.tick = tick;
  out.phase = phase;
  out.beat = beat;
  out.bar = bar;
  out.beatInBar = beat - bar * TUNE.clock.beatsPerBar;
  out.tpb = curTpb;
  out.bpm = (TICK_HZ * 60) / curTpb;
  out.rampStart = t.rampStart;
  out.sdStart = t.sdStart;
  out.cap = t.cap;
  return out;
}

/** True if a multiple of `every` (+offset) beats was crossed in (prevBeat, beat]. */
export function crossedBeat(prevBeat: number, beat: number, every: number, offset = 0): boolean {
  return Math.floor((beat - offset) / every) > Math.floor((prevBeat - offset) / every);
}
